import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { faReply } from '@fortawesome/free-solid-svg-icons/faReply';
import { faSpinner } from '@fortawesome/free-solid-svg-icons/faSpinner';
import { faXmark } from '@fortawesome/free-solid-svg-icons/faXmark';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';

import Button, { BUTTON_SIZES, BUTTON_TYPES } from '@buyside/components/presentation/Button/button';

import { messageUrl, messagesUrl } from '@buyside/util/urlHelper';
import { useIsMobile } from '@buyside/util/hooks/useIsMobile';

import ThumbsDown from '@assets/svgs/thumbs_down.svg';

import { IntroRequest, useUpdateIntroRequestStatusMutation } from '@interfaces/graphql-typings';

const { introRequestStatuses } = require('buyside/util/constantsFromRails.json');

import styles from './fundProfileCardIntroRequestStyles.scss';

const FundProfileCardIntroRequest: React.FC<{
  fundName: string;
  pendingIntroRequest: Pick<IntroRequest, 'id' | 'status' | 'messageThread'>;
}> = ({ fundName, pendingIntroRequest }) => {
  const isMobile = useIsMobile();
  const timeout = useRef<ReturnType<typeof setTimeout>>();

  const [closed, setClosed] = useState(false);
  const [status, setStatus] = useState(pendingIntroRequest.status);

  const searchParams = useMemo(() => new URLSearchParams(window.location.search), []);

  const [handleUpdateIntroRequestStatus, { loading }] = useUpdateIntroRequestStatusMutation();

  const handleClick = useCallback(
    async (status: string): Promise<void> => {
      try {
        const { data } = await handleUpdateIntroRequestStatus({
          variables: {
            status: status,
            introRequestId: pendingIntroRequest.id
          }
        });

        const newStatus = data?.updateIntroRequest?.introRequest?.status;

        if (newStatus) {
          setStatus(newStatus);
          timeout.current = setTimeout((): void => setClosed(true), 5000);
        }
      } catch (error) {
        if (error) throw error;
      }
    },
    [handleUpdateIntroRequestStatus, pendingIntroRequest.id]
  );

  useEffect(() => {
    return (): void => clearTimeout(timeout.current);
  }, []);

  useEffect(() => {
    if (searchParams.get('acceptedIntroRequestId') === pendingIntroRequest.id)
      handleClick(introRequestStatuses.accepted);
  }, [handleClick, pendingIntroRequest.id, searchParams]);

  const isStatusAccepted = status === introRequestStatuses.accepted;
  const isStatusDeclined = status === introRequestStatuses.hidden;

  if (closed || isStatusAccepted) return null;

  return (
    <div
      className={classNames(styles.introRequestWrapper, {
        [styles.declinedRequestWrapper]: isStatusDeclined
      })}
    >
      {isStatusDeclined ? (
        <>
          <FontAwesomeIcon icon={faXmark} />
          Fund Intro Declined.
        </>
      ) : (
        <>
          <p>{fundName} would like the opportunity to chat with your organization.</p>
          <div>
            <Button
              analyticsTag="open-reply-message"
              faIcon={faReply}
              disabled={loading}
              onClick={(): Promise<void> => handleClick(introRequestStatuses.accepted)}
              size={isMobile ? BUTTON_SIZES.TINY : BUTTON_SIZES.SMALL}
              text="Reply"
              type={BUTTON_TYPES.MAJOR}
              url={
                pendingIntroRequest.messageThread
                  ? messageUrl(pendingIntroRequest.messageThread)
                  : messagesUrl()
              }
            />
            <Button
              analyticsTag="ignore-request"
              icon={ThumbsDown}
              disabled={loading}
              onClick={(): Promise<void> => handleClick(introRequestStatuses.hidden)}
              size={isMobile ? BUTTON_SIZES.TINY : BUTTON_SIZES.SMALL}
              text="Decline"
              type={BUTTON_TYPES.DANGER}
            />
            {loading && <FontAwesomeIcon icon={faSpinner} spin />}
          </div>
        </>
      )}
    </div>
  );
};

export default FundProfileCardIntroRequest;
